import { createTheme, Input, rem, type MantineColorsTuple } from '@mantine/core';
import { tokens } from './tokens';

const scalePadBlue: MantineColorsTuple = [
  '#e8f4fc',
  '#d1e8f8',
  '#a3d1f1',
  '#6fb5e8',
  tokens.dark.accent.default,
  tokens.light.accent.default,
  '#1660BE',
  '#0f5f9e',
  '#0a4b7f',
  '#053760',
];

const warmGray: MantineColorsTuple = [
  tokens.light.bg.muted,
  tokens.light.border.default,
  '#D6D3D1',
  '#C8C5C2',
  tokens.light.text.tertiary,
  tokens.light.text.secondary,
  '#4A4A45',
  '#37352F',
  '#242320',
  tokens.light.text.default,
];

const meridianGreen: MantineColorsTuple = [
  tokens.light.success.light,
  '#B5F0C8',
  '#7CE49E',
  '#4AD87A',
  '#16B364',
  tokens.light.success.default,
  '#047857',
  '#065F46',
  '#064E3B',
  '#022C22',
];

const meridianAmber: MantineColorsTuple = [
  tokens.light.warning.light,
  '#FDE68A',
  '#FCD34D',
  '#FBBF24',
  '#F59E0B',
  tokens.light.warning.default,
  '#B45309',
  '#92400E',
  '#78350F',
  '#5C2A0A',
];

const meridianRed: MantineColorsTuple = [
  tokens.light.error.light,
  '#FECACA',
  '#FCA5A5',
  '#F87171',
  '#EF4444',
  tokens.light.error.default,
  '#B91C1C',
  '#991B1B',
  '#7F1D1D',
  '#651A1A',
];

const meridianViolet: MantineColorsTuple = [
  tokens.light.info.soft,
  '#E0D9F7',
  '#C9BCF0',
  '#B09EE8',
  '#9580E0',
  tokens.light.info.default,
  '#5B43BD',
  '#4A35A0',
  '#3A2982',
  '#2B1E63',
];

export const theme = createTheme({
  primaryColor: 'scalePadBlue',
  primaryShade: { light: 5, dark: 4 },
  colors: {
    scalePadBlue,
    warmGray,
    meridianGreen,
    meridianAmber,
    meridianRed,
    meridianViolet,
  },
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  fontFamilyMonospace: 'ui-monospace, "SF Mono", "Cascadia Code", monospace',
  headings: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontWeight: '600',
    sizes: {
      h1: { fontSize: rem(28), lineHeight: '1.25' },
      h2: { fontSize: rem(22), lineHeight: '1.25' },
      h3: { fontSize: rem(18), lineHeight: '1.25' },
      h4: { fontSize: rem(15), lineHeight: '1.25' },
    },
  },
  radius: {
    xs: rem(4),
    sm: rem(6),
    md: rem(8),
    lg: rem(10),
    xl: rem(16),
  },
  spacing: {
    xs: rem(4),
    sm: rem(8),
    md: rem(16),
    lg: rem(24),
    xl: rem(32),
  },
  defaultRadius: 'md',
  components: {
    Input: Input.extend({
      defaultProps: {
        radius: 'sm',
      },
    }),
    Button: {
      defaultProps: {
        radius: 'md',
        autoContrast: true,
      },
      styles: {
        root: {
          transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
          fontWeight: 500,
        },
      },
    },
    ActionIcon: {
      defaultProps: {
        radius: 'md',
      },
      styles: {
        root: {
          transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
        },
      },
    },
    Badge: {
      defaultProps: {
        radius: 'xl',
        variant: 'light',
      },
    },
    Card: {
      defaultProps: {
        radius: 'md',
        withBorder: false,
        padding: 'md',
      },
    },
    Table: {
      defaultProps: {
        verticalSpacing: 'sm',
        horizontalSpacing: 'md',
      },
    },
    Modal: {
      defaultProps: {
        radius: 'md',
        centered: true,
        overlayProps: { backgroundOpacity: 0.55, blur: 2 },
      },
      styles: {
        content: { backgroundColor: 'var(--surface-raised)' },
        header: { backgroundColor: 'var(--surface-raised)' },
        title: { fontWeight: 600, color: 'var(--text-primary)' },
      },
    },
    Drawer: {
      defaultProps: {
        radius: 'md',
        overlayProps: { backgroundOpacity: 0.55, blur: 2 },
      },
      styles: {
        content: { backgroundColor: 'var(--surface-base)' },
        header: { backgroundColor: 'var(--surface-base)' },
        title: { fontWeight: 600, color: 'var(--text-primary)' },
      },
    },
    Notification: {
      defaultProps: {
        radius: 'md',
      },
    },
  },
});
